<script>
  import {
    Label,
    Button,
    Input,
    Select,
    Card,
    Checkbox,
    Helper,
    Dropdown,
    Spinner,
    DropdownItem,
    ButtonGroup,
    InputAddon,
    Popover,
    Search,
    Modal,
  } from 'flowbite-svelte';
  import { InfoCircleOutline, ChevronDownOutline } from 'flowbite-svelte-icons';
  import {
    createMutation,
    useQueryClient,
    createQuery,
  } from '@tanstack/svelte-query';
  import { queryUserData } from './data';
  import { client } from './lib/client';
  import { navigate } from 'svelte-routing';
  import { _ } from 'svelte-i18n';
  import CuratedByCarousel from './CuratedByCarousel.svelte';

  const DRAFT_BASE_URL = {
    development: 'http://localhost:8001',
    staging: 'https://creators-staging.usebutton.com',
    production: 'https://creators.posttap.com',
  };

  const queryClient = useQueryClient();

  $: userQuery = queryUserData();

  export let editMode = false;
  export let pageData = {};
  export let socialHandle = '';
  export let bioLine1 = '';
  export let bioLine2 = '';
  export let selectedImage = '';
  export let categories = 'all';
  export let trackingIds = [];
  export let storeIds = [];
  export let initialUserHandle = '';
  let userHandle = initialUserHandle;
  export let destination = '';
  const initialPath = `${initialUserHandle}/${destination}`;
  export let successToast = () => {};
  export let errorToast = () => {};

  $: if (!userHandle && $userQuery?.data?.object?.profile?.instagram) {
    userHandle = $userQuery.data.object.profile.instagram;
  }

  $: if (userHandle !== socialHandle) {
    socialHandle = userHandle;
  }

  let errors = {};

  let linkType = 'all';
  if (!categories) {
    linkType = 'all';
  } else if (categories === 'product') {
    linkType = 'product';
  } else if (categories === 'storefront') {
    linkType = 'storefront';
  } else if (categories.includes(',')) {
    linkType = 'both';
  }

  let loading = false;

  const buildDraftUrl = (
    socialHandle,
    bioLine1,
    bioLine2,
    selectedImage,
    trackingIds,
    storeIds,
    linkType,
  ) => {
    const draftBaseUrl = DRAFT_BASE_URL[window.NODE_ENV] || '';
    const url = new URL(`${draftBaseUrl}/curated-by/draft`);
    url.searchParams.append('recent_lookback', '7');
    url.searchParams.append('creator_id', $userQuery?.data?.object.creatorID);
    url.searchParams.append('handle', socialHandle);
    url.searchParams.append('bioLine1', bioLine1 || '');
    url.searchParams.append('bioLine2', bioLine2 || '');
    url.searchParams.append('image_url', selectedImage);
    url.searchParams.append(
      'tracking_ids',
      trackingIds.includes('all') ? '' : trackingIds.join(','),
    );
    url.searchParams.append(
      'store_ids',
      storeIds.includes('all') ? '' : storeIds.join(','),
    );
    url.searchParams.append(
      'categories',
      {
        all: '',
        storefront: 'storefront',
        product: 'product',
        both: 'product,storefront',
      }[linkType] || '',
    );
    return url.toString();
  };

  let iframeUrl = buildDraftUrl(
    socialHandle,
    bioLine1,
    bioLine2,
    selectedImage,
    trackingIds,
    storeIds,
    linkType,
  );

  let debounceTimeout;
  $: {
    clearTimeout(debounceTimeout);
    debounceTimeout = setTimeout(() => {
      iframeUrl = buildDraftUrl(
        socialHandle,
        bioLine1,
        bioLine2,
        selectedImage,
        trackingIds,
        storeIds,
        linkType,
      );
    }, 300);
  }

  const getCreatorImages = async () => {
    const response = await client.get(`/api/curated/creator/attributes`);
    const { images } = response.data.object;
    return images;
  };

  const getStorefronts = async () => {
    const response = await client.get(`/api/curated/creator/storefronts`);
    return [
      ...response.data.objects.map((item) => ({
        value: item.shop_id,
        name: item.url,
      })),
    ];
  };

  const getTrackingIds = async () => {
    const response = await client.get(`/api/curated/creator/tracking-ids`);
    return [
      ...response.data.objects.map((item) => ({
        value: item,
        name: item,
      })),
    ];
  };

  $: userHandle = normalizedPathComponent(userHandle);
  $: destination = normalizedPathComponent(destination);

  function normalizedPathComponent(component) {
    if (!component) {
      return '';
    }
    const val = component
      .replace(/[^a-zA-Z0-9._-]/g, '') // allow letters, numbers, ., -, _
      .replace(/^\.+/, ''); // remove leading dots

    // Disallow "." and ".." as full handles
    if (val === '.' || val === '..') {
      return '';
    }
    return val;
  }

  let validationDebounceTimeout;
  let lastCheckedHandle = null;
  async function checkValidHandle(handle) {
    if (
      !handle ||
      (editMode && initialUserHandle === handle) ||
      handle === lastCheckedHandle
    ) {
      return;
    }

    clearTimeout(validationDebounceTimeout);
    validationDebounceTimeout = setTimeout(async () => {
      lastCheckedHandle = handle;

      try {
        await client.get(`/api/curated/handle/valid?handle=${handle}`);
        errors = {
          ...errors,
          userHandle: undefined,
        };
      } catch (e) {
        const err = e.response.data.error.message;
        let message = '';

        if (err === 'handle must be 3 or more characters') {
          message = $_('curatedby.form.userHandleTooShort', {
            default: 'Handles must be at least 3 characters long.',
          });
        } else if (err === 'handle exceeds 32 character limit') {
          message = $_('curatedby.form.userHandleTooLong', {
            default: 'Handles must be 32 characters or less.',
          });
        } else if (err === 'handle has already been reserved') {
          message = `${$_('curatedby.form.userHandleError1', {
            default: 'Another creator has claimed',
          })} '${handle}' ${$_('curatedby.form.userHandleError2', {
            default: '- please pick a different one!',
          })}`;
        }

        // show error message
        errors = {
          ...errors,
          userHandle: message,
        };
      }
    }, 300);
  }

  let lastCheckedPath = null;
  async function checkValidPath(userHandle, destination) {
    const path = `${userHandle}/${destination}`;
    // we need both fields filled in and
    if (
      !userHandle ||
      !destination ||
      (editMode && path === initialPath) ||
      path === lastCheckedPath
    ) {
      return;
    }

    clearTimeout(validationDebounceTimeout);
    validationDebounceTimeout = setTimeout(async () => {
      lastCheckedPath = path;

      try {
        await client.get(`/api/curated/page/path/valid?path=${path}`);
        errors = {
          ...errors,
          destination: undefined,
        };
      } catch (e) {
        // show error message
        errors = {
          ...errors,
          destination: $_('curatedby.form.destinationError', {
            default:
              'You have an existing CuratedBy page that matches both of these values - please change one of them so your URL is unique.',
          }),
        };
      }
    }, 300);
  }

  $: checkValidHandle(userHandle);
  $: checkValidPath(userHandle, destination);

  $: imagesQuery = createQuery({
    queryKey: ['creator-images'],
    queryFn: getCreatorImages,
  });

  $: trackingIdsQuery = createQuery({
    queryKey: ['tracking-ids'],
    queryFn: getTrackingIds,
  });

  $: storefrontQuery = createQuery({
    queryKey: ['storefronts'],
    queryFn: getStorefronts,
  });

  let storefrontSearch = '';
  let trackingIdsSearch = '';

  $: storefrontResults = ($storefrontQuery?.data || [])
    .filter((item) => item.name.includes(storefrontSearch))
    .slice(0, 7);

  $: trackingIdsResults = ($trackingIdsQuery?.data || [])
    .filter((item) => item.name.includes(trackingIdsSearch))
    .slice(0, 7);

  let selectAllTrackingIds = editMode;
  let selectAllStoreIds = editMode;

  // Select all trackingIds in the beginning
  $: if (
    $trackingIdsQuery.data &&
    trackingIds.length === 0 &&
    !selectAllTrackingIds
  ) {
    trackingIds = $trackingIdsQuery.data.map((item) => item.value);
    selectAllTrackingIds = true;
  }

  // Select all storeIds in the beginning
  $: if ($storefrontQuery.data && storeIds.length === 0 && !selectAllStoreIds) {
    storeIds = $storefrontQuery.data.map((item) => item.value);
    selectAllStoreIds = true;
  }

  const createHandle = async (pageData) => {
    try {
      const userHandleResponse = await client.get('/api/curated/handle/list');
      const userHandles = userHandleResponse.data.objects.map((obj) =>
        obj.handle.toLowerCase(),
      );
      if (!userHandles.includes(pageData.userHandle.toLowerCase())) {
        const handleParams = {
          handle: pageData.userHandle,
        };
        await client.post('/api/curated/handle', handleParams);
      }
    } catch (err) {
      console.error('Error creating handle:', err);
      throw err;
    }
  };

  const createUpdateCuratedPage = async (page) => {
    try {
      const pageParams = {
        handle: page.userHandle,
        slug: destination,
        configuration: {
          lookback: 30,
          recent_lookback: 7,
          categories: {
            storefront: 'storefront',
            product: 'product',
            both: 'product,storefront',
          }[linkType],
          bioLine1: page.bioLine1,
          bioLine2: page.bioLine2,
          image_url: selectedImage,
          trackingIds: page.trackingIds.includes('all')
            ? ''
            : page.trackingIds.join(','),
          storeIds: page.storeIds.includes('all')
            ? ''
            : page.storeIds.join(','),
          socialHandle: page.socialHandle,
        },
      };
      let response;
      if (editMode) {
        response = await client.patch(
          `/api/curated/page/${pageData.id}`,
          pageParams,
        );
      } else {
        response = await client.post('/api/curated/page', pageParams);
      }
      return response.data;
    } catch (err) {
      console.error('Error creating curated page:', err);
      throw err;
    }
  };

  const createHandleAndCuratedPage = async (pageData) => {
    try {
      if (!editMode) {
        await createHandle(pageData);
      }
      return await createUpdateCuratedPage(pageData);
    } catch (err) {
      console.error('Error in createHandleAndCuratedPage:', err);
      throw err;
    }
  };

  const mutation = createMutation({
    mutationKey: 'curated-by-page',
    mutationFn: createHandleAndCuratedPage,
    onMutate: () => {
      loading = true;
    },
    onSettled: () => {
      loading = false;
      queryClient.invalidateQueries(['curated-by-page']);
    },
    onError: () => {
      errorToast(
        editMode
          ? $_('curatedby.form.pageUpdateError', {
              default: 'Failed to update page',
            })
          : $_('curatedby.form.pageCreateError', {
              default: 'Failed to create page',
            }),
      );
      return null;
    },
    onSuccess: () => {
      try {
        successToast(
          editMode
            ? $_('curatedby.form.pageUpdateSuccess', {
                default: 'Page Updated',
              })
            : $_('curatedby.form.pageCreateSuccess', {
                default: 'Page Created',
              }),
        );
        navigate('/curated-by');
      } catch (e) {
        console.error(e);
      }
    },
  });

  let isDisabled = true;
  $: isDisabled =
    loading ||
    Object.values(errors).filter((value) => !!value).length ||
    !userHandle ||
    !destination ||
    !selectedImage ||
    !socialHandle;

  function handleSubmit() {
    $mutation.mutate({
      socialHandle,
      bioLine1,
      bioLine2,
      selectedImage,
      linkType,
      trackingIds,
      storeIds,
      userHandle,
      destination,
    });
  }

  let isUploading = false;
  let isImageUploaded = false;

  let isPreviewVisible = false;

  async function uploadFile(file) {
    isUploading = true;
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await fetch('/api/curated/page/image', {
        method: 'POST',
        body: formData,
      });

      if (!res.ok) {
        throw new Error('Upload failed');
      }

      const data = await res.json();
      selectedImage = data.object.tempUrl;
      isImageUploaded = true;
    } catch (err) {
      console.error('Upload error:', err);
    } finally {
      isUploading = false;
    }
  }

  async function handleDrop(event) {
    event.preventDefault();
    const file = event.dataTransfer.files[0];

    if (!file) return;
    if (file.type !== 'image/png' && file.type !== 'image/jpeg') {
      return;
    }

    await uploadFile(file);
  }

  async function handleFileUpload(event) {
    const file = event.target.files[0];
    if (!file) return;

    await uploadFile(file);
  }

  let imageSection = 'upload-image';
  let storefrontSelectedImage = null;

  $: if (imageSection === 'storefront') {
    selectedImage = storefrontSelectedImage;
  }

  function initialImage() {
    selectedImage = $imagesQuery.data?.[0]?.image_url;
    storefrontSelectedImage = $imagesQuery.data?.[0]?.image_url;
  }

  $: {
    if (!editMode && $imagesQuery.isSuccess) {
      initialImage();
    }
  }

  function changeImageSection(newImageSection) {
    imageSection = newImageSection;
  }
</script>

<Modal bind:open={isPreviewVisible} class="[&_div]:overflow-hidden">
  <div class="w-[375px] origin-top-left scale-75">
    <div
      class="relative rounded-[3rem] bg-gray-800 p-3 shadow-2xl shadow-gray-400/60"
    >
      <div
        class="absolute left-1/2 top-0 h-6 w-40 -translate-x-1/2 transform rounded-b-3xl bg-gray-800"
      ></div>
      <div class="h-[720px] w-full overflow-hidden rounded-[2.5rem] bg-white">
        <iframe src={iframeUrl} class="h-full w-full"></iframe>
      </div>
    </div>
  </div>
</Modal>
<div>
  <CuratedByCarousel />
  <Card class="mb-4 w-full max-w-none">
    <div class="flex gap-2 md:gap-8">
      <div class="flex-1 space-y-8">
        <div class="mb-8">
          <h2
            id="customize-url-heading"
            class="mb-6 flex w-fit items-center text-base font-semibold text-gray-900"
          >
            {$_('curatedby.form.customizeURLHeading', {
              default: 'Customize your URL',
            })}
            <InfoCircleOutline
              size="sm"
              class="ml-2 text-gray-900"
              id="label-tooltip-help"
            />
            <Popover
              triggeredBy="#customize-url-heading"
              class="z-20 w-full max-w-md text-sm font-medium text-black"
            >
              {$_('curatedby.form.popover.text1', {
                default: 'All CuratedBy links will start with',
              })} <span class="text-blue-500 underline">curatedby.us</span>
              {$_('curatedby.form.popover.text2', {
                default:
                  "- there are 2 additional aspects to the URL: 1) your social handle, which we prefill automatically for you from your Instagram Handle in your profile and 2) a follower-friendly navigation path (we like 'topamazonlinks'). If you need to update your social handle, please do so in the Profile section of the dashboard!",
              })}
            </Popover>
          </h2>

          <div class="mb-4 flex items-center gap-2">
            <span class="block font-medium text-gray-900 dark:text-gray-300"
              >curatedby.us/</span
            >
            <Input
              type="text"
              bind:value={userHandle}
              on:blur={() => {
                if (socialHandle === '') {
                  socialHandle = userHandle;
                }
              }}
              placeholder="your handle"
              class="flex-1"
              color={errors.userHandle ? 'red' : 'base'}
              disabled={editMode}
            />
            <span class="text-gray-600">/</span>
            <Input
              type="text"
              bind:value={destination}
              color={errors.destination ? 'red' : 'base'}
              disabled={editMode}
              placeholder="topamazonlinks"
              class="flex-1"
            />
          </div>
          {#if errors.userHandle}
            <Helper class="text-sm" color="red">
              {errors.userHandle}
            </Helper>
          {:else if errors.destination}
            <Helper class="text-sm" color="red">
              {errors.destination}
            </Helper>
          {:else if !editMode}
            <p class="text-sm text-gray-500">
              {$_('curatedby.form.helperText', {
                default:
                  "Add a descriptive word that tells followers what they'll find here (e.g., 'topamazonlinks', 'favoritebooks', 'techdeals')",
              })}
            </p>
          {/if}
        </div>
        <div class="mb-8">
          <h2
            id="label-personalize-tooltip"
            class="mb-6 flex w-fit items-center text-base font-semibold text-gray-900"
          >
            {$_('curatedby.form.personalizeYourPageHeading', {
              default: 'Personalize your page',
            })}
            <InfoCircleOutline size="sm" class="ml-2 text-gray-900" />
            <Popover
              triggeredBy="#label-personalize-tooltip"
              class="z-20 w-full max-w-md text-sm font-medium text-black"
            >
              {$_('curatedby.form.personalizePopover', {
                default:
                  "We pull your bio image automatically for you from your Amazon storefront, it's up to you to personalize the rest! If you want your page to only show certain link types, like just product links, you can select 'just product links' from the dropdown. Any Amazon URL with 'shop' in the url will be included as a Storefront link. You can also limit only certain links to display on your CuratedBy page by selecting just the tracking IDs or storefront IDs that are in the links you want to show.",
              })}
            </Popover>
          </h2>
          <div>
            <Label class="mb-2">
              {$_('curatedby.form.profilePhotoHeader', {
                default: 'Profile Photo',
              })}
            </Label>
            <p class="mb-3 text-xs italic">
              {$_('curatedby.form.imageSelectionHeader', {
                default: 'Choose an option to upload your Profile Photo:',
              })}
            </p>
            <div class="flex flex-row gap-4">
              <button
                on:click={() => changeImageSection('upload-image')}
                class={`w-[8.5rem] rounded-md py-3 text-center text-sm ${imageSection === 'upload-image' ? 'bg-[#D6E4FF] text-[#1A56DB]' : 'bg-[#D9D9D9] text-gray-800'}`}
              >
                {$_('curatedby.form.uploadImageSectionButton', {
                  default: 'Upload Image',
                })}
              </button>
              <button
                on:click={() => changeImageSection('storefront')}
                class={`w-[8.5rem] rounded-md py-3 text-center text-sm ${imageSection === 'storefront' ? 'bg-[#D6E4FF] text-[#1A56DB]' : 'bg-[#D9D9D9] text-gray-800'}`}
              >
                {$_('curatedby.form.storefrontButton', {
                  default: 'Storefront',
                })}
              </button>
            </div>
          </div>
          {#if imageSection === 'upload-image'}
            <div
              class="my-4 flex h-40 w-full flex-col items-center justify-center space-y-4 rounded-md border-2 border-dashed p-4"
              on:drop={handleDrop}
              on:dragenter={(e) => e.preventDefault()}
              on:dragleave={(e) => e.preventDefault()}
              on:dragover={(e) => e.preventDefault()}
            >
              {#if isUploading}
                <Spinner />
              {:else}
                <svg
                  width="19"
                  height="23"
                  viewBox="0 0 19 23"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-8 w-8 text-gray-500"
                >
                  <path
                    d="M1.83331 11.4998V19.1665C1.83331 19.6748 2.03525 20.1623 2.39469 20.5218C2.75414 20.8812 3.24165 21.0832 3.74998 21.0832H15.25C15.7583 21.0832 16.2458 20.8812 16.6053 20.5218C16.9647 20.1623 17.1666 19.6748 17.1666 19.1665V11.4998M13.3333 5.74984L9.49998 1.9165M9.49998 1.9165L5.66665 5.74984M9.49998 1.9165V14.3748"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                <p class="text-sm text-gray-600">
                  {$_('curatedby.form.dragAndDropImage', {
                    default: 'Drag & drop your image here',
                  })}
                </p>
                <Label
                  for="file_input"
                  class="cursor-pointer rounded-md bg-[#1A56DB] px-6 py-3 text-sm text-white"
                >
                  {#if isImageUploaded}
                    {$_('curatedby.form.replaceFile', {
                      default: 'Replace File',
                    })}
                  {:else}
                    {$_('curatedby.form.uploadFile', {
                      default: 'Upload File',
                    })}
                  {/if}
                </Label>
                <input
                  on:change={handleFileUpload}
                  id="file_input"
                  type="file"
                  accept="image/jpeg,image/png,image/jpg"
                  hidden
                />
              {/if}
            </div>
          {:else}
            <div class="my-4">
              {#if $imagesQuery.isSuccess && $imagesQuery.data.length > 0}
                <Select
                  bind:value={storefrontSelectedImage}
                  items={$imagesQuery.data.map((e) => {
                    return {
                      name: e.shop_id,
                      value: e.image_url,
                    };
                  })}
                />
              {:else}
                <div class="flex flex-col items-center justify-center">
                  <p class="text-sm">
                    {$_('curatedby.form.noStorefronts', {
                      default:
                        'No Storefront links found. Please upload a profile image',
                    })}
                  </p>
                </div>
              {/if}
            </div>
          {/if}

          <div class="space-y-6">
            <div>
              <Label class="mb-3 block">
                {$_('curatedby.form.socialHandle', {
                  default: 'Social Handle',
                })}
              </Label>
              <ButtonGroup class="w-full">
                <InputAddon>@</InputAddon>
                <Input
                  on:keypress={(e) => {
                    if (e.key === '@') {
                      e.preventDefault();
                    }
                  }}
                  type="text"
                  bind:value={socialHandle}
                  class="w-full"
                  disabled={socialHandle !== null && socialHandle !== ''}
                />
              </ButtonGroup>
            </div>

            <div class="grid grid-cols-2 gap-6">
              <div>
                <Label class="mb-3 block">
                  {$_('curatedby.form.bioLine1', {
                    default: 'Bio Line 1',
                  })}
                </Label>
                <Input
                  type="text"
                  bind:value={bioLine1}
                  placeholder={$_('curatedby.form.bioLine1.placeholder', {
                    default: 'Up to 25 characters',
                  })}
                  maxlength="25"
                  class="w-full"
                />
              </div>
              <div>
                <Label class="mb-3 block">
                  {$_('curatedby.form.bioLine2', {
                    default: 'Bio Line 2',
                  })}
                </Label>
                <Input
                  type="text"
                  bind:value={bioLine2}
                  placeholder={$_('curatedby.form.bioLine2.placeholder', {
                    default: 'Up to 25 characters',
                  })}
                  maxlength="25"
                  class="w-full"
                />
              </div>
            </div>
          </div>
        </div>
        <div class="mb-8">
          <h2
            class="mb-6 flex items-center text-base font-semibold text-gray-900"
          >
            {$_('curatedby.form.specifyYourLinks', {
              default: 'Specify your links',
            })}
          </h2>
          <div class="grid grid-cols-2 gap-6">
            <div>
              <Label class="mb-3 block">
                {$_('curatedby.form.linkTypes', {
                  default: 'Link Types',
                })}
              </Label>
              <Select bind:value={linkType} class="w-full">
                <option value="all">
                  {$_('curatedby.form.option1', {
                    default: 'All',
                  })}
                </option>
                <option value="product"
                  >{$_('curatedby.form.option2', {
                    default: 'Just Product Links',
                  })}
                </option>
                <option value="storefront">
                  {$_('curatedby.form.option3', {
                    default: 'Just Storefront Links',
                  })}
                </option>
                <option value="both">
                  {$_('curatedby.form.option4', {
                    default: 'Only Product & Storefront Links',
                  })}
                </option>
              </Select>
            </div>
            <div>
              <Label class="mb-3 block">
                {$_('curatedby.form.trackingIds.label', {
                  default: 'Tracking IDs to include',
                })}
              </Label>
              {#if !$trackingIdsQuery.isLoading && !$trackingIdsQuery.isError}
                <div class="relative" id="tracking-dropdown">
                  <Button
                    class="flex w-full items-center justify-between border bg-white !p-2.5 text-left"
                    color={linkType === 'storefront' ? 'light' : 'disabled'}
                    disabled={linkType === 'storefront'}
                  >
                    <span class="text-gray-500">
                      {`${trackingIds.length} ${$_(
                        'curatedby.form.trackingIds.selected',
                        {
                          default: 'Selected',
                        },
                      )}`}
                    </span>
                    <ChevronDownOutline size="xs"></ChevronDownOutline>
                  </Button>
                  <Dropdown class="z-50 w-full" containerClass="w-full z-50">
                    <DropdownItem>
                      <Search
                        placeholder={$_(
                          'curatedby.form.trackingIds.searchPlaceholder',
                          {
                            default: 'Search Tracking IDs',
                          },
                        )}
                        bind:value={trackingIdsSearch}
                      />
                    </DropdownItem>
                    {#each trackingIdsResults as item}
                      <DropdownItem class="w-full">
                        <div class="py-1">
                          <Checkbox
                            checked={trackingIds.includes(item.value)}
                            on:change={(e) => {
                              if (e.target.checked) {
                                trackingIds = [...trackingIds, item.value];
                              } else {
                                trackingIds = trackingIds.filter(
                                  (id) => id !== item.value,
                                );
                              }
                            }}
                          >
                            {item.name}
                          </Checkbox>
                        </div>
                      </DropdownItem>
                    {/each}
                    <div
                      class="flex flex-col items-center justify-center gap-1"
                    >
                      <p class="text-center text-xs text-gray-500">
                        {$_('curatedby.form.trackingIds.text1', {
                          default: 'Showing',
                        })}
                        {trackingIdsResults.length}
                        {$_('curatedby.form.trackingIds.text2', {
                          default: 'of',
                        })}
                        {$trackingIdsQuery.data.length}
                        {$_('curatedby.form.trackingIds.text3', {
                          default: 'results',
                        })}
                      </p>
                      {#if trackingIds.length < $trackingIdsQuery?.data?.length}
                        <Button
                          size="xs"
                          on:click={() => {
                            trackingIds = ($trackingIdsQuery?.data || []).map(
                              (item) => item.value,
                            );
                          }}
                          >{$_('curatedby.form.trackingIds.selectAll', {
                            default: 'Select All',
                          })}
                        </Button>
                      {:else if trackingIds.length === $trackingIdsQuery?.data?.length}
                        <Button
                          size="xs"
                          on:click={() => {
                            trackingIds = [];
                          }}
                          >{$_('curatedby.form.trackingIds.deselectAll', {
                            default: 'Deselect All',
                          })}
                        </Button>
                      {/if}
                    </div>
                  </Dropdown>
                </div>
              {/if}
            </div>
            <div class="col-span-2 mt-6">
              <Label class="mb-3 block">
                {$_('curatedby.form.storefrontIds.label', {
                  default: 'Storefront IDs to include',
                })}
              </Label>
              {#if !$storefrontQuery.isLoading && !$storefrontQuery.isError}
                <div id="storefront-dropdown" class="relative">
                  <Button
                    class="flex w-full items-center justify-between rounded-lg border bg-white p-2.5 text-left"
                    color={'light'}
                    disabled={linkType === 'product'}
                  >
                    <span class="text-gray-500">
                      {`${storeIds.length} ${$_(
                        'curatedby.form.storefrontIds.selected',
                        {
                          default: 'Selected',
                        },
                      )}`}
                    </span>
                    <ChevronDownOutline size="xs"></ChevronDownOutline>
                  </Button>
                  <Dropdown
                    class="relative w-full"
                    containerClass="w-full z-10"
                  >
                    <DropdownItem>
                      <Search
                        placeholder={$_('curatedby.form.storefrontIds.search', {
                          default: 'Search Storefront IDs',
                        })}
                        bind:value={storefrontSearch}
                      />
                    </DropdownItem>
                    {#each storefrontResults as item}
                      <DropdownItem>
                        <div class="py-1">
                          <Checkbox
                            checked={storeIds.includes(item.value)}
                            on:change={(e) => {
                              if (e.target.checked) {
                                storeIds = [...storeIds, item.value];
                              } else {
                                storeIds = storeIds.filter(
                                  (id) => id !== item.value,
                                );
                              }
                            }}
                          >
                            {item.name}
                          </Checkbox>
                        </div>
                      </DropdownItem>
                    {/each}
                    <div
                      class="flex flex-col items-center justify-center gap-1"
                    >
                      <p class="text-center text-xs text-gray-500">
                        {$_('curatedby.form.storefrontIds.showing', {
                          default: 'Showing',
                        })}
                        {storefrontResults.length}
                        {$_('curatedby.form.storefrontIds.of', {
                          default: 'of',
                        })}
                        {$storefrontQuery.data.length}
                        {$_('curatedby.form.storefrontIds.results', {
                          default: 'results',
                        })}
                      </p>
                      {#if storeIds.length < $storefrontQuery?.data?.length}
                        <Button
                          size="xs"
                          on:click={() => {
                            storeIds = ($storefrontQuery?.data || []).map(
                              (item) => item.value,
                            );
                          }}
                          >{$_('curatedby.form.storefrontIds.selectAll', {
                            default: 'Select All',
                          })}
                        </Button>
                      {:else if storeIds.length === $storefrontQuery?.data?.length}
                        <Button
                          size="xs"
                          on:click={() => {
                            storeIds = [];
                          }}
                          >{$_('curatedby.form.storefrontIds.deselectAll', {
                            default: 'Deselect All',
                          })}
                        </Button>
                      {/if}
                    </div>
                  </Dropdown>
                </div>
              {/if}
            </div>
          </div>
        </div>
        <Button
          class="w-full xl:hidden"
          color="alternative"
          on:click={() => {
            isPreviewVisible = true;
          }}
        >
          {$_('curatedby.mobile.viewPreview', {
            default: 'View Preview',
          })}
        </Button>
        <Button
          class="mt-8 flex w-full items-center justify-center rounded-lg bg-blue-600 py-3 text-white hover:bg-blue-700"
          on:click={handleSubmit}
          disabled={isDisabled}
        >
          {#if editMode}
            {loading
              ? $_('curatedby.form.button.updating', {
                  default: 'Updating...',
                })
              : $_('curatedby.form.button.updatePage', {
                  default: 'Update Page',
                })}
          {:else}
            <span class="mr-2">+</span>
            {loading
              ? $_('curatedby.form.button.creating', {
                  default: 'Creating...',
                })
              : $_('curatedby.form.button.createPage', {
                  default: 'Create Page',
                })}
          {/if}
        </Button>
      </div>

      <div class="hidden flex-1 md:pl-8 xl:block">
        <h2 class="mb-6 text-base font-semibold text-gray-900">
          {$_('curatedby.form.heading', {
            default: 'Preview your CuratedBy Page',
          })}
        </h2>
        <div class="w-[375px] origin-top-left scale-75">
          <div
            class="relative rounded-[3rem] bg-gray-800 p-3 shadow-2xl shadow-gray-400/60"
          >
            <div
              class="absolute left-1/2 top-0 h-6 w-40 -translate-x-1/2 transform rounded-b-3xl bg-gray-800"
            ></div>
            <div
              class="h-[720px] w-full overflow-hidden rounded-[2.5rem] bg-white"
            >
              <iframe src={iframeUrl} class="h-full w-full"></iframe>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Card>
</div>
